import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppLayout } from "@/components/AppLayout";
import { motion } from "framer-motion";
import { Upload, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BatchProcess = ({ user }) => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState('');
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/configs`, { withCredentials: true }).then(r => setConfigs(r.data)).catch(() => toast.error('Failed to load configurations'));
  }, []);

  const handleFilesChange = (e) => {
    const selected = Array.from(e.target.files);
    const valid = selected.filter(f => f.type === 'application/pdf' || f.type.startsWith('image/'));
    if (valid.length !== selected.length) toast.warning('Some files were skipped');
    setFiles(valid); setResults(null);
  };

  const convertToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
  });

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    if (!selectedConfig) { toast.error('Select a configuration'); return; }
    if (files.length === 0) { toast.error('Select files'); return; }
    setProcessing(true); setResults(null);
    try {
      const filesData = await Promise.all(files.map(async (file) => ({
        file_id: `file_${Date.now()}_${Math.random()}`, file_name: file.name,
        file_base64: await convertToBase64(file), is_pdf: file.type === 'application/pdf'
      })));
      const response = await axios.post(`${BACKEND_URL}/api/process-batch`, { config_id: selectedConfig, files: filesData }, { withCredentials: true });
      setResults(response.data);
      toast.success(`Processed ${response.data.completed} of ${response.data.total} invoices!`);
      setFiles([]); setSelectedConfig('');
      const fi = document.getElementById('batch-file-upload');
      if (fi) fi.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AppLayout user={user} activeNav="/batch-process">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
        <Card className="mb-6 bg-white/90 backdrop-blur-sm border border-stone-200/60">
          <CardHeader>
            <CardTitle className="font-heading">Batch Process</CardTitle>
            <CardDescription>Upload multiple invoices at once</CardDescription>
          </CardHeader>
          <CardContent>
            {configs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-stone-500 mb-4">No configurations found</p>
                <Button onClick={() => navigate('/configurations')} className="bg-sage-500 hover:bg-sage-600 rounded-xl">Set Up Configuration First</Button>
              </div>
            ) : (
              <form onSubmit={handleBatchSubmit} className="space-y-6">
                <div>
                  <Label>Select Configuration</Label>
                  <Select value={selectedConfig} onValueChange={setSelectedConfig}>
                    <SelectTrigger data-testid="batch-config-select" className="border-2 border-stone-200 rounded-xl"><SelectValue placeholder="Choose a folder configuration" /></SelectTrigger>
                    <SelectContent>{configs.map(c => <SelectItem key={c.config_id} value={c.config_id}>{c.drive_folder_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Upload Multiple Files</Label>
                  <label htmlFor="batch-file-upload" className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-stone-200 border-dashed rounded-xl cursor-pointer hover:border-sage-400 transition-colors">
                    <div className="space-y-1 text-center">
                      {files.length > 0 ? (
                        <><CheckCircle className="mx-auto h-10 w-10 text-green-500" /><p className="text-sm text-stone-600">{files.length} file(s) selected</p></>
                      ) : (
                        <><Upload className="mx-auto h-10 w-10 text-stone-400" /><p className="text-sm text-stone-600"><span className="font-medium text-sage-600">Click to upload multiple files</span></p><p className="text-xs text-stone-400">PDF or Images</p></>
                      )}
                    </div>
                  </label>
                  <input id="batch-file-upload" type="file" data-testid="batch-file-upload" accept="application/pdf,image/*" onChange={handleFilesChange} multiple className="sr-only" />
                  {files.length > 0 && (
                    <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                      {files.map((f, i) => <p key={i} className="text-xs text-stone-500 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />{f.name}</p>)}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={processing || !files.length || !selectedConfig} data-testid="batch-process-button" className="bg-sage-500 hover:bg-sage-600 rounded-xl">
                    {processing ? `Processing ${files.length} file(s)...` : `Process ${files.length} Invoice(s)`}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {results && (
          <Card className="border-2 border-green-200 bg-green-50/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="font-heading text-green-800">Batch Complete</CardTitle>
              <CardDescription>{results.completed} of {results.total} processed{results.errors > 0 ? ` (${results.errors} errors)` : ''}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {results.results.map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                    <span className="text-sm text-stone-700 truncate flex-1">{r.file_name}</span>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ml-2 ${r.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </AppLayout>
  );
};

export default BatchProcess;
